/**
 * One playbook run: where it is in its plan, every step it took (with what
 * it produced, what it cost, what it is waiting for), and the controls an
 * administrator has — pause, resume (with Retry/Skip after an uncertain
 * write), cancel. Polls while the run is alive.
 */
import { useCallback, useEffect, useState } from 'react'
import type { AgentsRuntime, PlaybookRun, RunStep } from './contract'
import { C, Chip, cardStyle, formatWhen, labelStyle } from '../jobs/ui'
import { RunStateChip, credits, smallBtn } from './bits'

const LIVE = new Set(['queued', 'running', 'waiting_human', 'waiting_event'])

const STEP_TONE: Record<string, 'ok' | 'err' | 'warn' | 'info' | 'muted'> = {
  done: 'ok', failed: 'err', waiting: 'warn', running: 'info',
  interrupted: 'muted', skipped: 'muted', abandoned: 'muted',
}

function preview(v: unknown): string {
  if (v == null) return ''
  try {
    const s = typeof v === 'string' ? v : JSON.stringify(v)
    return s.length > 280 ? `${s.slice(0, 280)}…` : s
  } catch {
    return String(v)
  }
}

export default function RunDetail({ runtime, runId, onBack }: {
  runtime: AgentsRuntime
  runId: string
  onBack(): void
}) {
  const [run, setRun] = useState<PlaybookRun | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [open, setOpen] = useState<string>('')
  const [transcript, setTranscript] = useState<Record<string, unknown>[] | null>(null)

  const load = useCallback(async () => {
    try {
      setRun(await runtime.getRun(runId))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the run.')
    }
  }, [runtime, runId])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    if (!run || !LIVE.has(run.state)) return undefined
    const id = window.setInterval(() => { void load() }, 4000)
    return () => window.clearInterval(id)
  }, [run, load])

  const act = async (op: 'pause' | 'resume' | 'cancel', uncertain?: 'retry' | 'skip') => {
    setBusy(op)
    try {
      await runtime.control(runId, op, uncertain)
      runtime.notify?.(op === 'cancel' ? 'Cancel requested.' : op === 'pause' ? 'Pause requested.' : 'Resumed.', 'success')
      await load()
    } catch (err) {
      runtime.notify?.(err instanceof Error ? err.message : `Could not ${op} the run.`, 'error')
    } finally {
      setBusy('')
    }
  }

  const showTranscript = async (step: RunStep) => {
    const key = step.step_path.split('/').pop() || step.step_path
    try {
      setTranscript(await runtime.transcript(runId, key))
      setOpen(step.step_id)
    } catch (err) {
      runtime.notify?.(err instanceof Error ? err.message : 'No transcript.', 'error')
    }
  }

  if (error) return <div style={{ color: C.err, padding: 16 }}>{error}</div>
  if (!run) return <div style={{ color: C.ink2, padding: 16 }}>Loading…</div>

  const doneIds = new Set((run.steps || []).filter((s) => s.state === 'done').map((s) => s.step_path.split('/')[0]))
  const pct = run.credits_cap ? Math.min(100, (run.credits_spent / run.credits_cap) * 100) : 0
  const uncertain = run.state === 'suspended' && run.suspend_reason === 'write_uncertain'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: C.ink }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={smallBtn()}>← All runs</button>
        <div style={{ fontWeight: 700, fontSize: '1.0625rem' }}>{run.playbook_title || run.playbook}</div>
        <RunStateChip state={run.state} label={run.state_label} />
        <span style={{ flex: 1 }} />
        {LIVE.has(run.state) && (
          <button type="button" disabled={!!busy} onClick={() => void act('pause')} style={smallBtn()}>Pause</button>
        )}
        {(run.state === 'paused' || (run.state === 'suspended' && !uncertain)) && (
          <button type="button" disabled={!!busy} onClick={() => void act('resume')} style={smallBtn(true)}>Resume</button>
        )}
        {uncertain && (
          <>
            <button type="button" disabled={!!busy} onClick={() => void act('resume', 'retry')} style={smallBtn(true)}>Retry the write</button>
            <button type="button" disabled={!!busy} onClick={() => void act('resume', 'skip')} style={smallBtn()}>It was saved — skip</button>
          </>
        )}
        {!['completed', 'failed', 'cancelled'].includes(run.state) && (
          <button type="button" disabled={!!busy} onClick={() => void act('cancel')}
            style={{ ...smallBtn(), color: C.err }}>Cancel</button>
        )}
      </div>

      <div style={{ ...cardStyle, padding: 12, display: 'grid', gap: 10,
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <div><div style={labelStyle}>Persona</div><div>{run.persona_name || '—'}</div></div>
        <div><div style={labelStyle}>Started</div><div>{formatWhen(run.started_at || run.queued_at)}</div></div>
        <div><div style={labelStyle}>Trigger</div><div>{run.trigger_kind}</div></div>
        <div>
          <div style={labelStyle}>Credits</div>
          <div>{credits(run.credits_spent)} of {credits(run.credits_cap)}</div>
          <div style={{ height: 6, background: C.s2, borderRadius: 3, marginTop: 4 }}>
            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3,
              background: pct > 90 ? C.err : pct > 70 ? C.warn : C.accent }} />
          </div>
        </div>
      </div>

      {(run.error_text || run.summary) && (
        <div style={{
          borderRadius: 8, padding: '8px 12px', fontSize: '0.875rem',
          background: run.error_text ? C.errWeak : C.okWeak, color: run.error_text ? C.err : C.ok,
        }}>{run.error_text || run.summary}</div>
      )}

      {run.plan && run.plan.length > 0 && (
        <div>
          <div style={{ ...labelStyle, marginBottom: 6 }}>Plan</div>
          <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 3,
            fontSize: '0.9125rem' }}>
            {run.plan.map((p) => (
              <li key={p.id} style={{ color: doneIds.has(p.id) ? C.ok : C.ink }}>
                {doneIds.has(p.id) ? '✓ ' : ''}{p.title} <span style={{ color: C.faint }}>· {p.kind.replace('_', ' ')}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Steps taken</div>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {(run.steps || []).length === 0 && (
            <div style={{ padding: 12, color: C.ink2, fontSize: '0.9125rem' }}>No steps yet.</div>
          )}
          {(run.steps || []).map((s) => (
            <div key={s.step_id} style={{ borderTop: `1px solid ${C.line}`, padding: '8px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: C.faint, fontSize: '0.8125rem', minWidth: 22 }}>{s.seq}</span>
                <span style={{ fontWeight: 600, fontSize: '0.9125rem' }}>{s.title || s.step_path}</span>
                {s.iter_key && <span style={{ color: C.faint, fontSize: '0.8125rem' }}>item {s.iter_key}</span>}
                <Chip tone={STEP_TONE[s.state] || 'muted'}>{s.state}</Chip>
                <span style={{ color: C.faint, fontSize: '0.8125rem' }}>{s.kind.replace('_', ' ')}</span>
                <span style={{ flex: 1 }} />
                {s.credits > 0 && <span style={{ color: C.ink2, fontSize: '0.8125rem' }}>{credits(s.credits)} cr</span>}
                {s.kind === 'reason' && (
                  <button type="button" onClick={() => void showTranscript(s)} style={smallBtn()}>Reasoning</button>
                )}
              </div>
              {s.error_text && <div style={{ color: C.err, fontSize: '0.8625rem', marginTop: 4 }}>{s.error_text}</div>}
              {s.result != null && !s.error_text && (
                <div style={{ color: C.ink2, fontSize: '0.8425rem', marginTop: 4, fontFamily: 'monospace',
                  wordBreak: 'break-word' }}>{preview(s.result)}</div>
              )}
              {open === s.step_id && transcript && (
                <div style={{ marginTop: 6, background: C.s2, borderRadius: 6, padding: 8,
                  fontSize: '0.8425rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {transcript.map((m, i) => (
                    <div key={i}>
                      <b>{String(m.role === 'event' ? m.event : m.role)}</b>{' '}
                      {preview(m.text || m.summary || m.name)}
                    </div>
                  ))}
                  <button type="button" onClick={() => setOpen('')} style={{ ...smallBtn(), alignSelf: 'flex-start' }}>Close</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {(run.links || []).length > 0 && (
        <div>
          <div style={{ ...labelStyle, marginBottom: 6 }}>Links sent outside the application</div>
          <div style={{ ...cardStyle, padding: '4px 12px' }}>
            {(run.links || []).map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', flexWrap: 'wrap',
                fontSize: '0.875rem', borderTop: i ? `1px solid ${C.line}` : 'none' }}>
                <span>{l.recipient}</span>
                <span style={{ color: C.ink2 }}>{l.title}</span>
                <span style={{ flex: 1 }} />
                {l.used_at ? <Chip tone="ok">Answered {formatWhen(l.used_at)}</Chip>
                  : <Chip tone="warn">Expires {formatWhen(l.expires_at)}</Chip>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
